import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { financeApi, type Bucket, type FinanceOverview } from "../../api/finance";
import { marketsApi } from "../../api/markets";
import { useAuth } from "../../auth/AuthContext";
import { KPI } from "../../components/primitives.jsx";
import { FinanceFilters, type FiltersValue, type Redosled } from "./FinanceFilters";
import { AgingChart, AgingStrip, bucketLabel } from "./AgingChart";
import { IznosSaValutama, iznos } from "./CurrencyBreakdown";
import { presetRange } from "./usePeriod";
import { money, fmtDate } from "./money";
import { TableSkeleton } from "../../components/Loading";
import { Pager } from "../../components/Pager";
import { AmountByCurrency } from "../../components/AmountByCurrency";
import { intlLocale } from "../../lib/format";
import { countryName } from "../../i18n/countries";
import { usePage } from "../../lib/usePage";
import { Refreshing, useFilteredQuery } from "../../lib/useFilteredQuery";

/** Small text under a value: what the figure is and what it leaves out. */
const napomena: React.CSSProperties = { fontSize: 11, fontWeight: 400, lineHeight: 1.35, color: "var(--ink-3)", marginTop: 2 };

export default function FinanceOverviewPage() {
  const { t, i18n } = useTranslation();
  const locale = intlLocale(i18n.resolvedLanguage);
  const { hasPermission } = useAuth();
  const [markets, setMarkets] = useState<{ id: string; name: string }[]>([]);
  const init = useMemo<FiltersValue>(() => ({ preset: "ytd", ...presetRange("ytd") }), []);
  const [filters, setFilters] = useState<FiltersValue>(init);
  const [bucket, setBucket] = useState<Bucket | undefined>(undefined);
  // Najveći dug prvo: spisak potraživanja se gleda da bi se znalo koga zvati, a to je kupac
  // na vrhu. Bez izbora baza je vraćala redove bilo kojim redom, pa je i strana dva bila lutrija.
  const [sort, setSort] = useState<Redosled>("DUG_OPADAJUCE");
  const [page, setPage] = usePage();
  const SIZE = 50;
  // Prazno znači „sve valute", i tada je svaki iznos preračunat u evre; izabrana valuta znači
  // da su iznosi tačni, onakvi kakvi stoje na računima.
  const valuta = filters.currency;

  // Market dropdown is for director/admin (markets:view). SALES has no markets:view
  // and only sees assigned customers, so they get country + period filters only.
  useEffect(() => {
    if (!hasPermission("markets:view")) return;
    marketsApi.overviewList().then(setMarkets).catch(() => undefined);
  }, [hasPermission]);

  // Jedan odgovor je šest upita nad celom knjigom računa, pa se spisak traži kad se kucanje smiri,
  // a dok novi odgovor stiže na ekranu ostaju redovi koji su već tu. Kostur se vidi samo dok nema
  // ničega, i greška prekida čekanje — inače se filter ukucan preko nedovršenog zahteva vidi kao
  // kostur koji treperi i spisak koji nikad ne dođe.
  const upit = useFilteredQuery<FinanceOverview>(
    (q) =>
      financeApi.overview({
        from: filters.from, to: filters.to, marketId: filters.marketId,
        // Više država ide kao jedan spisak, isto kao što tržište već ide spiskom šifara.
        country: filters.countries?.join(","),
        currency: filters.currency, bucket, q: q || undefined, sort, page, size: SIZE,
      }),
    {
      keys: [filters.from, filters.to, filters.marketId, filters.countries?.join(","),
             filters.currency, bucket, sort, page],
      text: filters.q ?? "",
      // Polje pretrage već drži otkucano 300 ms pre nego što filter uopšte promeni (`SearchBox`),
      // pa ovde ostaje samo ostatak pauze.
      delay: 250,
    },
  );
  const data = upit.data;
  const naDan = data?.asOf
    ? t("finance.asOf", "stanje na dan {{datum}}", { datum: fmtDate(data.asOf) })
    : undefined;

  const onChange = (v: FiltersValue) => {
    const range = v.preset === "custom" ? { from: v.from, to: v.to } : presetRange(v.preset);
    setFilters({ ...v, ...range });
    setPage(0);   // novi filter, nova prva strana: ostati na sedmoj strani starog spiska nema smisla
  };

  const onBucket = (b: Bucket | undefined) => {
    setBucket(b);
    setPage(0);
  };

  // Spisak država stiže uz podatke, jer se sa pedeset učitanih redova ne vidi šta sve postoji.
  // Dok se prva strana ne učita, filter se drži prethodnog spiska umesto da nestane pa se vrati.
  const [countries, setCountries] = useState<string[]>([]);
  const [currencies, setCurrencies] = useState<string[]>([]);
  useEffect(() => {
    if (!data) return;
    setCountries(data.availableCountries ?? []);
    // Izabrana valuta ostaje u spisku i kad je filter po kašnjenju izbaci iz opsega, da se
    // korisnik ne zatekne u izboru koji više ne može da poništi.
    const stigle = data.availableCurrencies ?? [];
    setCurrencies(
      filters.currency && !stigle.includes(filters.currency)
        ? [...stigle, filters.currency]
        : stigle,
    );
  }, [data, filters.currency]);

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1>{t("finance.title", "Finance")}</h1>
          <div className="sub">{t("finance.subtitle.overview", "Receivables across your customers")}</div>
        </div>
      </div>

      {/* Oznaka osvežavanja stoji uz filtere, a ne preko tabele: spiner nad spiskom sakrio bi
          upravo one redove koji zato i ostaju na ekranu. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <FinanceFilters value={filters} markets={markets} countries={countries}
                        currencies={currencies} onChange={onChange}
                        sort={sort} onSortChange={(r) => { setSort(r); setPage(0); }} />
        <Refreshing on={upit.refreshing} />
      </div>

      {upit.error && <div className="empty">{upit.error}</div>}
      {upit.skeleton && <TableSkeleton rows={8} cols={6} />}
      {data && (
        <>
          {/* Dva reda po četiri, po smislu: novac gore, pokazatelji dole. Osam kartica u jednom
              nizu ostavljalo je osmu samu u drugom redu; osim toga su unutar jednog reda sve
              bile visoke koliko i najviša, pa je „Kupci" bila prazna do pola. `kpi-grid` je
              raspored koji ostatak aplikacije već koristi: četiri kolone, dve na užem ekranu. */}
          <div className="kpi-grid" style={{ marginBottom: 0 }}>
            <KPI
              label={t("finance.kpi.receivables", "Total receivables")}
              value={<IznosSaValutama eur={data.totals.receivablesEur} byCurrency={data.totals.byCurrency ?? []}
                                      uzmi={(v) => v.balance} locale={locale} currency={valuta} />}
              delta={undefined} trend={undefined} icon={undefined}
            />
            {/* Overdue is counted on the day the outgoing invoices reach, which can be earlier than
                today; the tile and the bands below name that day. */}
            <KPI
              label={t("finance.kpi.overdue", "Overdue")}
              value={<>
                <IznosSaValutama eur={data.totals.overdueEur} byCurrency={data.totals.byCurrency ?? []}
                                 uzmi={(v) => v.overdue} locale={locale} currency={valuta} />
                {naDan && <div style={napomena}>{naDan}</div>}
              </>}
              delta={undefined} trend={undefined} icon={undefined}
            />
            <KPI
              label={t("finance.aging.notDue", "Nedospelo")}
              value={<IznosSaValutama eur={data.totals.notDueEur} byCurrency={data.totals.byCurrency ?? []}
                                      uzmi={(v) => v.notDue} locale={locale} currency={valuta} />}
              delta={undefined} trend={undefined} icon={undefined}
            />
            {/* Invoices only. The opening balance and the other postings are debits too, but summed
                as turnover they would set a year that carries an opening balance against one that
                does not, so they are named under the value. DSO divides by this same figure. */}
            <KPI
              label={t("finance.kpi.turnover", "Turnover")}
              value={<>
                <span style={iznos}>{money(data.totals.turnoverEur, valuta ?? "EUR")}</span>
                <div style={napomena}>{t("finance.kpi.turnoverHint", "računi, sa PDV-om")}</div>
                {((data.totals.carriedPostedEur ?? 0) > 0 || (data.totals.otherPostedEur ?? 0) > 0) && (
                  <div style={napomena}>
                    {t("finance.notInvoiced", "Nije uračunato: preneto iz ranijih godina {{prenos}}, ostala knjiženja {{ostalo}}", {
                      prenos: money(data.totals.carriedPostedEur ?? 0, valuta ?? "EUR"),
                      ostalo: money(data.totals.otherPostedEur ?? 0, valuta ?? "EUR"),
                    })}
                  </div>
                )}
              </>}
              delta={undefined} trend={undefined} icon={undefined}
            />
          </div>

          {/* Prosečna naplata i koncentracija su ono što se iz spiska ne vidi: koliko se dugo
              čeka na novac i koliko je duga sabijeno u nekoliko kupaca. */}
          <div className="kpi-grid" style={{ marginBottom: 0 }}>
            <KPI
              label={t("finance.kpi.dso", "Prosečna naplata")}
              value={<span style={iznos}>
                {data.totals.dso == null ? "—" : t("finance.kpi.days", "{{n}} dana", { n: data.totals.dso })}
              </span>}
              delta={undefined} trend={undefined} icon={undefined}
            />
            <KPI
              label={t("finance.kpi.top5", "Pet najvećih dužnika")}
              value={<span style={iznos}>
                {data.totals.top5Pct == null ? "—" : `${data.totals.top5Pct}%`}
              </span>}
              delta={undefined} trend={undefined} icon={undefined}
            />
            <KPI label={t("finance.kpi.customers", "Customers")} value={<span style={iznos}>{String(data.totals.companyCount)}</span>} delta={undefined} trend={undefined} icon={undefined} />
            <KPI label={t("finance.kpi.withOverdue", "With overdue")} value={<span style={iznos}>{String(data.totals.companiesWithOverdue)}</span>} delta={undefined} trend={undefined} icon={undefined} />
          </div>

          <AgingChart totals={data.totals} selected={bucket} onSelect={onBucket} currency={valuta ?? "EUR"}
                      naDan={naDan} />

          {!valuta && data.totals.unconvertedCount > 0 && (
            <div className="empty" style={{ textAlign: "left" }}>
              {t(
                "finance.unconverted",
                "{{count}} amount(s) are kept in a currency with no exchange rate on file and are not included in the totals above.",
                { count: data.totals.unconvertedCount },
              )}
            </div>
          )}

          {data.companies.length === 0 ? (
            <div className="empty">{t("finance.overview.empty", "No customers match the selected filters.")}</div>
          ) : (
            <>
            {bucket && (
              <div style={{ color: "var(--ink-3)", fontSize: 13 }}>
                {t("finance.aging.filtered", "Prikazani su kupci sa otvorenim iznosom u grupi „{{grupa}}“.",
                   { grupa: bucketLabel(bucket, t) })}
              </div>
            )}
            <Pager page={data.page} size={data.size} total={data.totalElements} onPage={setPage} />
            <div className="card" style={{ overflow: "hidden", padding: 0 }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t("finance.overview.company", "Customer")}</th>
                  <th>{t("finance.filter.country", "Country")}</th>
                  <th style={{ textAlign: "right" }}>{t("finance.kpi.balance", "Balance")}</th>
                  <th style={{ textAlign: "right" }}>{t("finance.aging.notDue", "Nedospelo")}</th>
                  <th style={{ textAlign: "right" }}>{t("finance.kpi.overdue", "Overdue")}</th>
                  <th style={{ textAlign: "right" }}>{t("finance.kpi.turnover", "Turnover")}</th>
                  <th>{t("finance.overview.lastPayment", "Last payment")}</th>
                </tr>
              </thead>
              <tbody>
                {data.companies.map((c) => (
                  <tr key={c.companyId} className="row-link">
                    <td><Link to={`/finance/companies/${c.companyId}`} style={{ color: "var(--brand-primary)", textDecoration: "none" }}>{c.name}</Link></td>
                    <td>{c.country ? countryName(c.country, i18n.resolvedLanguage ?? "sr") : "—"}</td>
                    <td style={{ textAlign: "right" }}>
                      <AmountByCurrency eur={c.balanceEur} byCurrency={c.balanceByCurrency}
                                        locale={locale} currency={valuta ?? "EUR"} />
                    </td>
                    <td style={{ textAlign: "right" }}>{money(c.notDueEur, valuta ?? "EUR")}</td>
                    <td style={{ textAlign: "right", color: c.overdueEur > 0 ? "var(--err)" : undefined }}>
                      <div>{money(c.overdueEur, valuta ?? "EUR")}</div>
                      {/* Broj kaže koliko kupac duguje, traka koliko je taj dug star. */}
                      <div style={{ display: "flex", justifyContent: "flex-end" }}><AgingStrip row={c} currency={valuta ?? "EUR"} /></div>
                    </td>
                    <td style={{ textAlign: "right" }}>{money(c.turnoverEur, valuta ?? "EUR")}</td>
                    <td>{fmtDate(c.lastPaymentDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <Pager page={data.page} size={data.size} total={data.totalElements} onPage={setPage} />
            </>
          )}
        </>
      )}
    </div>
  );
}
